<template>
  <div class="articles-container">
    <div id="heading1" class="article-section ul-padding-inline-start">
      <h2 class="h3">
        Linear Regression Calculator
      </h2>
      <p>Our linear regression calculator uses the Ordinary Least Squares (OLS) method to generate the simple linear regression equation. It’s a powerful tool to <span class="bolded-texts">predict the dependent variable based on one independent variable</span>. For example, you can analyze how a basketball player’s performance is affected by height, age, or weight.</p>
      <p>In addition, this regression calculator provides an analysis of variance (ANOVA) table that breaks down the sources of variation. The tool can calculate the correlation coefficient, coefficient of determination, and standard error for simple linear regression. It also performs tests to determine whether the results are statistically significant.</p>
      <p>This article presents an in-depth overview of the theory behind simple linear regression calculation, interpretation, and usage. We start with the meaning of regression, define all related statistical concepts to aid your understanding, provide various ways to perform the calculations, and help you interpret the obtained results. It contains all the necessary information to fully utilize our simple linear regression calculator.</p>
    </div>

    <div id="heading2" class="article-section ul-padding-inline-start">
      <h3 class="introduction h4">
        What Is Regression Analysis?
      </h3>
      <p>Regression analysis is one of the most widely used prediction methods—applied whenever we have a causal relationship between a dependent variable and one or more independent variables. The dependent variable is the value we try to explain or predict. The independent variables (or predictors) are explanatory and used to make the prediction.</p>
      <p>Regression analysis aims to develop a mathematical model representing the relationship between the independent and dependent variables. Building a model that accurately represents the observed (known) values would allow us to predict future values.</p>
      <p class="lr-bottom-margin-40">
        The word ’regression’ was coined by Francis Galton in the 19th century, who observed that the heights of descendants of tall ancestors tended to regress toward the average size. Initially used to describe this phenomenon, it became a key concept in statistical analysis and gave rise to the development of regression analysis as a statistical method.
      </p>
      <h4 class="h5 bottom-margin-32">
        Types of Regression
      </h4>
      <p class="lr-bottom-margin-40">
        Different types of regression analysis techniques exist, such as <span class="bolded-texts">linear regression</span>, <span class="bolded-texts">logistic regression</span>, and <span class="bolded-texts">polynomial regression</span>.
      </p>
      <h5 class="h6">
        Linear Regression
      </h5>
      <p>
        A linear regression is a linear approximation of a causal relationship between two or more variables. It’s a fundamental <nuxt-link to="/tutorials/machine-learning-tutorials/introduction-machine-learning/" target="_blank">
          machine learning
        </nuxt-link> method and an excellent starting point for every aspiring data scientist's advanced analytical learning path.
      </p>
      <p class="bolded-texts">
        Simple Linear Regression Example
      </p>
      <p class="lr-bottom-margin-40">
        Note the following example illustrating the relationship between the amount of rainfall and the number of umbrellas sold. In this case, we have only one explanatory variable, known as a <span class="bolded-texts">simple linear regression</span>.
      </p>
      <p class="first-img-heading">
        Simple Linear Regression
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-01.webp" alt="Simple linear regression graph.">
      <p>On the scatter plot, the dots correspond to the observed data, each representing one month for two years or 24 months in total. For each month, we record the total rainfall depth expressed in millimeters (the independent variable) on the horizontal axis and the number of umbrellas sold (the dependent variable) on the vertical axis. We can see a positive relationship between the two variables—the more significant the rainfall, the higher the number of umbrellas sold, and vice versa.</p>
      <p>Regression analysis aims to build a model that allows us to draw a line approximating the real (observed) values. In other words, we hypothesize that the independent variable (rainfall) is a good predictor of the dependent variable (number of umbrellas sold), and we draw a line that reflects the relationship between the two variables. So, the regression line illustrates where our predicted values lie, and the data points are the actual (observed) values. In this case, we have a linear relationship, so the line of best fit is straight. But as we'll see below, we could also have a non-linear relationship.</p>
      <p>If our prediction is accurate, the predicted values will approximate the observed values—so the line would be at a minimal distance from the observed values. This is an example of a good regression model that effectively captures the relationship between the dependent and independent variables.</p>
      <p>Such a model would allow us to make predictive questions, such as: Given a particular x value, what is the expected value of the y-variable? In the context of our example, we can formulate the following question: Knowing the amount of rainfall for September, how many umbrellas will be sold in that month?</p>
      <p class="lr-bottom-margin-40">
        When there are two or more independent variables to predict a single outcome, we have <span class="bolded-texts">multivariate regression</span>, unlike simple linear regression.
      </p>
      <p class="bolded-texts">
        Multiple Linear Regression Example
      </p>
      <p>Outcomes could depend on two, three, or even 20 factors. Multiple regressions allow us to address the complexity of problems. The more variables you have, the more factors you must consider in a model. In the previous example, we could add more predictor variables, such as the temperature, the price of umbrellas, or the sale of raincoats, to predict how many umbrellas will be sold.</p>
      <p>Although the number of independent variables is virtually unlimited, matters become more complicated as it increases. Therefore, one must be mindful of statistical best practices to avoid mistakes.</p>
      <p class="lr-bottom-margin-40">
        Multivariate regression can be used for predictions and determining how the variables relate to one another and which variables matter the most when explaining a phenomenon.
      </p>
      <p>So, what does a regression analysis tell you?</p>
      <p class="lr-bottom-margin-40">
        Regression is instrumental when the independent variables represent factors that can be controlled, which is not the case for rainfall. In the previous example, rainfall and the price of umbrellas are much better predictors than the temperature and number of raincoats sold. That alone is a valuable insight because it allows us to ignore the temperature and the number of raincoats in future sales analyses and forecasts.
      </p>
      <h5 class="h6">
        Non-Linear Regression
      </h5>
      <p>Traditionally, statisticians use linear regressions when they have a numeric outcome variable and one or more predictor variables, but these won't always be numeric.</p>
      <p>While linear regression builds a solid foundation for your analysis, real-world problems often require more sophisticated or <span class="bolded-texts">nonlinear models</span>. Such models may be <span class="bolded-texts">quadratic</span>, <span class="bolded-texts">exponential</span>, and <span class="bolded-texts">logistic</span>.</p>
      <p class="lr-bottom-margin-40">
        With them, we model data by a nonlinear function of the independent and dependent variables.
      </p>
      <p class="bolded-texts">
        Logistic Regression Example
      </p>
      <p><span class="bolded-texts">Logistic regression</span> implies that the possible outcomes are not numerical but categorical or binary.</p>
      <p>It helps us answer questions like, “Will a customer buy the same product next year?” or “Do you engage your prospects on social media?” These variables can only take on two values: “yes” or “no.”</p>
      <p>A binary outcome variable could also be an event that may or may not occur.</p>
      <p>In a business context, decision-making often boils down to a yes-or-no verdict. With logistic regression, however, we can make a much more fundamental forecast by determining whether the customer will buy. Using linear regression, we can predict the price a customer would pay.</p>
      <p class="lr-bottom-margin-40">
        Consider the following example of a logistic model that predicts whether you’ll be admitted to a university based on your SAT score. The permissible output values in our model must fall between 0 and 1. The number 0 means ‘Denied,’ while 1 denotes ‘Admitted.’
      </p>
      <p class="second-img-heading">
        Logistic Regression
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-02.webp" alt="Logistic regression graph.">
      <p class="bolded-texts">
        Polynomial Regression Example
      </p>
      <p class="lr-bottom-margin-40">
        <span class="bolded-texts">Polynomial regression</span> is another type of regression model one must be familiar with, where the relationship between the dependent and independent variables is also non-linear—e.g., the relationship between height and weight. As you can see, with polynomial regressions, the curve doesn’t necessarily need to go in one direction.
      </p>
      <p class="third-img-heading">
        Polynomial Regression
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-03.webp" alt="Polynomial Regression graph.">
      <p class="lr-bottom-margin-40">
        The following demonstrates the relationship between professional football players’ age and salary, confirming this.
      </p>
      <p class="third-img-heading">
        Polynomial Regression
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-04.webp" alt="Polynomial Regression graph, football players age and salary example.">
      <p class="lr-bottom-margin-40">
        To summarize, <span class="bolded-texts">linear regression</span> is one of the most widely used techniques because it models the relationship between two quantitative variables and provides a straight-line relationship between them. <span class="bolded-texts">Logistic regression</span>, on the other hand, is used when the dependent variable is categorical and binary. <span class="bolded-texts">Polynomial</span> regression models the relationship between variables with a polynomial function of a degree greater than one.
      </p>
      <p class="bolded-texts">
        When should I use regression analysis?
      </p>
      <p>Regression analysis is a powerful statistical method that helps to model and understand the relationship between variables and provides a framework for making predictions based on data.</p>
      <p>It provides a way to analyze and interpret data and make predictions based on that data. It’s widely used in economics, finance, marketing, and healthcare to gain insight into complex phenomena and make informed decisions.</p>
      <p>The definitions above help you determine which type of analysis to perform depending on your data. You should perform a simple linear regression analysis to determine the linear relationship between a dependent and one independent variable. And the current linear regression calculator allows you to quickly and easily obtain the equation, calculations, and line of best fit.</p>
    </div>

    <div id="heading3" class="article-section ul-padding-inline-start">
      <h3 class="introduction h4">
        How to Find the Linear Regression Equation
      </h3>
      <p>Regression analysis aims to create a model that accurately represents the relationship between the independent and dependent variables.</p>
      <p>The regression equation effectively represents the mathematical relationship between the independent and dependent variables (outcome). The linear regression line is the visual representation of this relationship.</p>
      <p>The linear regression equation includes the following:</p>
      <p class="lr-formulas formulas-overflow">
        \[y=b_0+b_1x_1+\varepsilon\]
      </p>
      <p><span class="inline-formulas">\[y\]</span> – dependent variable</p>
      <p><span class="inline-formulas">\[x_1\]</span> – independent variable</p>
      <p><span class="inline-formulas">\[b_0\]</span> – a constant / intercept</p>
      <p><span class="inline-formulas">\[b_1\]</span> – coefficient  – quantifies the effect of <span class="inline-formulas">\[x_1\]</span> on <span class="inline-formulas">\[y\]</span></p>
      <p class="lr-bottom-margin-40">
        <span class="inline-formulas">\[ε\]</span> – error of estimation
      </p>
      <p class="lr-bottom-margin-40">
        The following is a graphical representation of the linear regression model:
      </p>
      <p class="fourth-img-heading">
        Geometrical Representation of the
      </p>
      <p class="fourth-img-heading">
        Linear Regression Model
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-05.webp" alt="Geometrical Representation of the Linear Regression Model.">
      <p>Where:</p>
      <p><span class="inline-formulas">\[x_i\]</span> – value of x for observation i</p>
      <p><span class="inline-formulas">\[\widehat{y\ }\]</span> – the value predicted by the regression line</p>
      <p><span class="inline-formulas">\[b_0\]</span> – a constant - the intercept of the regression line with the y axis</p>
      <p class="lr-bottom-margin-40">
        <span class="inline-formulas">\[b_1\]</span> – the slope of the regression line shows how much y changes for each unit change of x
      </p>
      <p>Here e, represents the estimator of the error—i.e., the distance between the observed values and the regression line.</p>
      <p>The objective of the regression is to plot the line that best fits the cloud of dots—i.e., the line of best fit that is at a minimal distance from all data points. This is known as the Ordinary Least Squares (OLS) method, widely used in regression analysis for estimating the parameters of the linear regression model. The goal is to find the slope and intercept values of the regression line that minimize the sum of the squared differences between the observed values of the dependent variable and the predicted values from the regression equation. In other words, it estimates the slope of the regression line and the intercept , which provide the best fit according to the least squares method.</p>
      <p>We use the following formula for the <span class="bolded-texts">linear regression slope</span> (<span class="inline-formulas">\[b_1\]</span>):</p>
      <p class="lr-formulas formulas-overflow">
        \[b_1=\frac{\sum_{i=1}^{n}(x_i-\bar{x})(y_i-\bar{y})}{\sum_{i=1}^{n}(x_i-\bar{x})^2}\]
      </p>
      <p>Where:</p>
      <p>n is the number of observations and</p>
      <p>x and y are the sample means of the predictor variable and the dependent variable, respectively.</p>
      <p>And we use the following equation to obtain the <span class="bolded-texts">intercept of the regression line with the y-axis</span>:</p>
      <p class="lr-formulas formulas-overflow">
        \[b_0=\bar{y}-b_1\bar{x}\]
      </p>
      <p>Where:</p>
      <p>y is the mean of the dependent variable.</p>
      <p>b1 is the slope of the regression line, representing the change in the dependent variable for a one-unit increase in the predictor variable.</p>
      <p>x is the mean of the predictor variable.</p>
      <p>Don’t worry if this is overwhelming—our line of best-fit calculator does all this for you. But knowing how to calculate the linear regression can help you better understand the results.</p>
      <p>Generally, the higher the absolute value of b, the steeper the regression curve.</p>
      <p>Besides, the sign of b indicates the direction of the relationship between Y and X. When b is positive, the regression line shows an upward slope. An increase of X results in the rise of Y. A negative b coefficient, however, illustrates a downward slope. In this case, if X moves up, Y goes down correspondingly.</p>
    </div>

    <div id="heading4" class="article-section ul-padding-inline-start">
      <h3 class="introduction h4">
        Interpretation of Linear Regression Results
      </h3>
      <p class="lr-bottom-margin-40">
        Our linear regression calculator briefly interprets your findings with the results and calculations. But to fully understand regression analysis, you must grasp several key concepts we define below.
      </p>
      <h4 class="h5 bottom-margin-32">
        Decomposition of Variability
      </h4>
      <p>Now that we know how to use the simple linear regression calculator and obtain the equation coefficients, we must explore the determinants of a good regression. We’ll start by decomposing the variability in regression analysis. The word ‘variability’ refers to the dispersion of data points around the regression line. In other words, it measures how much the dependent variable (Y) deviates from the predicted values (Ŷ) based on the regression model.</p>
      <p>In regression analysis, decomposition of variability helps us understand the sources of variation in the data and quantify their contributions to the overall variability. It is an essential aspect of regression analysis as it helps in assessing the goodness of fit of the regression model and understanding the relationships between variables.</p>
      <p class="bottom-margin-32">
        The variability is decomposed into three components. These are the sum of squares total (SST), the sum of squares regression (SSR), and the sum of squares error (SSE).
      </p>
      <img class="img bottom-margin-32" src="../../../../assets/images/calculators/linear-regression/linear-regression-06.webp" alt="Sums of squares total, squares regression and squares error.">
      <p>Mathematically, SST is equal to SSR + SSE:</p>
      <p class="formulas-overflow bottom-margin-32">
        \[SST=SSR+SSE\]
      </p>
      <p>The total variability of the dataset is equal to the variability explained by the regression line plus the unexplained variability, known as error.</p>
      <p class="formulas-overflow bottom-margin-32">
        \[\sum_{i=1}^{n}{(y_i-\bar{y})^2=\sum_{i=1}^{n}{({\hat{y}}_i-\bar{y})^2+\sum_{i=1}^{n}\varepsilon_i^2}}\]
      </p>
      <img class="img bottom-margin-32" src="../../../../assets/images/calculators/linear-regression/linear-regression2-07.webp" alt="Total, explained and unexplained variability formula.">
      <p>Note more information below about the three terms and how they relate to the linear regression model.</p>
      <p>The sum of squares (SST) total is the squared differences between the observed dependent variable and its mean. Think of it as the dispersion of the observed variables around the mean. It’s a measure of the total variability of the dataset. To that end, another notation of the term you might come across is TSS (total sum of squares).</p>
      <p>The sum of squares total (SST) = total sum of squares (TSS) – measures the total variability of the dataset:</p>
      <p class="formulas-overflow">
        \[SST=\sum_{i=1}^{n}{(y_i-\bar{y})}^2\]
      </p>
      <p>Where:</p>
      <p><span class="inline-formulas">\[y_i\]</span> – observed dependent variable</p>
      <p><span class="inline-formulas">\[\bar{y}\]</span> – mean of the dependent variable</p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-08.webp" alt="Formula and graph for sum of squares total.">
      <p>The second determinant of variability is the sum of squares due to regression (SSR)—the sum of the differences between the predicted value and the mean of the dependent variable. In essence, it describes how well your line fits the data. If the value of SSR is equal to the sum of squares total, it means your regression model captures all the observed variability and is perfect. Once again, we should mention that another standard notation of the sum of squares due to regression is ESS (explained sum of squares).</p>
      <p>The sum of squares regression (SSR) = explained sum of squares (ESS) – measures the explained variability by the regression line:</p>
      <p class="formulas-overflow">
        \[SSR=\sum_{i=1}^{n}{({\hat{y}}_i-\bar{y})}^2\]
      </p>
      <p>Where:</p>
      <p><span class="inline-formulas">\[\hat{y}_i\]</span> – the predicted value of the dependent variable</p>
      <p><span class="inline-formulas">\[\bar{y}\]</span> – mean of the dependent variable</p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-09.webp" alt="Formula and graph for sum of squares regression.">
      <p>The last term to consider is the sum of squares error (SSE). The error is the difference between the observed value and the predicted value. The smaller the error, the better the estimation power of the regression. That’s why we typically want to minimize the error. This determinant is often called RSS (residual sum of squares), where Residual denotes remaining or unexplained. It becomes even more confusing when some people refer to it as SSR—which makes it unclear whether we are talking about the sum of squares due to regression or the sum of squared residuals. Neither of these is universally adopted, so the confusion remains, and we must live with it.</p>
      <p>The sum of squares error (SSE) = residual sum of squares (RSS) – measures the unexplained variability by the regression:</p>
      <p class="formulas-overflow">
        \[SSE=\sum_{i=1}^{n}\varepsilon_i^2\]
      </p>
      <p>Where:</p>
      <p><span class="inline-formulas">\[ε_i\]</span> – the difference between the actual value of the dependent variable and the predicted value</p>
      <p class="inline-formulas">
        \[ε_i=y_i-\hat{y}_i\]
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-10.webp" alt="Formula and graph for sum of squares error.">
      <p>Given a constant total variability, a lower error means better regression. Conversely, a higher error leads to less robust regression. That makes sense, right? And that’s what you must remember, no matter the notation.</p>
      <p class="lr-bottom-margin-40">
        Information about the decomposition of variability is typically presented in the Analysis of Variance (ANOVA) table, which is part of many statistical tools and software, including this one. Our calculator generates the SSE, SST, and SSR along with the other results.
      </p>
      <h4 class="h5 bottom-margin-32">
        Coefficient of Determination
      </h4>
      <p>After decomposing the total variability in regression analysis, we must measure how well the model fits the data. This allows us to determine whether the regression equation does a good job of explaining changes in the dependent variable.</p>
      <p class="bottom-margin-32">
        The regression model fits the data well when the differences between the observations and the predicted values are relatively small. You cannot trust the results if these variances are too significant or the model is biased.
      </p>
      <img class="img bottom-margin-32" src="../../../../assets/images/calculators/linear-regression/linear-regression-11.webp" alt="Y-one and Y-two difference graph.">
      <p class="bottom-margin-32">
        There are several ways to evaluate the ‘goodness of fit,’ but the most fundamental is to check the <span class="bolded-texts">R-squared</span> (the <span class="bolded-texts">coefficient of determination</span>).
      </p>
      <img class="img bottom-margin-32" src="../../../../assets/images/calculators/linear-regression/linear-regression-12.webp" alt="Observed values graph.">
      <p>The coefficient of determination represents the proportion of variance in the dependent variable that can be explained by the independent variable in a regression model. It’s equal to the ratio of the explained variance (SSR) to the total variance (SST):</p>
      <p class="formulas-overflow">
        \[R^2=\frac{SSR}{SST}\]
      </p>
      <p class="bottom-margin-32">
        In simple linear regression models, an R-squared statistic is always between 0 and 1, respectively, between 0% and 100%.
      </p>
      <img class="img bottom-margin-32" src="../../../../assets/images/calculators/linear-regression/linear-regression-13.webp" alt="R-squared visual representation graph.">
      <p>If the R-squared is equal to 0, the model does not explain any of the variances in the dependent variable. In other words, the model adds no value to your analysis, and you should refrain from using it to make predictions.</p>
      <p class="bottom-margin-32">
        On the other hand, if the R-squared is 1, the model explains the whole variance in the dependent variable. Its predictions perfectly fit the data because all the observations fall precisely on the regression line. In practice, however, you’re unlikely to see a regression model with an R-squared of 1. If you do, ask a statistics expert to closely examine the model and the data.
      </p>
      <p class="four-graphs-img-heading">
        Comparison of R-Squared for Different Linear Models
      </p>
      <p class="four-graphs-img-heading-second">
        (Same Data Set)
      </p>
      <img class="img bottom-margin-32" src="../../../../assets/images/calculators/linear-regression/linear-regression-14.webp" alt="Comparison of R-Squared for Different Linear Models graph.">
      <img class="img bottom-margin-32" src="../../../../assets/images/calculators/linear-regression/linear-regression-15.webp" alt="Low and high graph for R to second power.">
      <p>When interpreting the R-squared of a simple linear regression, it’s always worth visualizing the data with a scatter plot. R-squared should be small if the dots do not form a thick cloud around the straight regression line. The closer the dots are to the regression line, the denser the cloud and the higher the R-squared statistic.</p>
      <p>If, by contrast, the data are dispersed, the R-squared might be misleading. In that case, you should double-check the results with a data expert.</p>
      <p class="bottom-margin-32">
        Our calculator generates the R-squared, so you can easily judge your model’s accuracy.
      </p>
      <p class="bolded-texts">
        What is a good R-squared value?
      </p>
      <p>So what is considered a good R-squared value, and at what level can you trust a model?</p>
      <p>As we know, an R squared of 1 would mean your model explains the entire data variability. Unfortunately, regressions explaining the variability as a whole are rare. What you will typically observe are values ranging from 0.2 to 0.9.</p>
      <p>Some claim that the threshold is 0.70. If a model returns an R-squared of 0.70, you can make predictions based on it. A value of R-squared below 0.70 indicates that the model does not fit well.</p>
      <p>In practice, however, the properties of R-squared are not as clear-cut as one may think. Generally, a higher R-squared indicates a better fit for the model, producing more accurate predictions. For example, a model explaining 70% of the variance is much better than the one explaining 30%.</p>
      <p>Nevertheless, such a conclusion is not necessarily correct. Ultimately, whether or not you can trust a particular R-squared value depends on various factors, including the <span class="bolded-texts">sample size</span>, <span class="bolded-texts">granularity</span>, and <span class="bolded-texts">type of data</span> employed.</p>
      <p>Most of the time, the more observations, the lower the R-squared—that’s how vital the <span class="bolded-texts">sample size</span> is.</p>
      <p>Regarding <span class="bolded-texts">granularity</span>, models based on case-level data have lower R-squared statistics than those found on aggregated data, such as city or country information. So, keep that in mind when analyzing the regression statistics.</p>
      <p>The <span class="bolded-texts">data type employed</span> in the model is another determinant you should consider. For example, when the variables are categorical or count, the R-squared is typically lower than continuous data.</p>
      <p>Furthermore, what qualifies as a “good” R-squared value also depends on the field of research. For instance, studies that explain human behavior tend to have lower R-squared values than those dealing with natural phenomena. This is simply because people are more challenging to predict than stars, molecules, cells, viruses, etc.</p>
      <p>Finally, a “good” R-squared may be defined differently depending on the objective of the analysis.</p>
      <p>If the purpose is to predict the dependent variable, then a low R-squared could cause problems, and you may need to abstain from using the model. How high the R-squared needs to be depends on the desired precision of the prediction.</p>
      <p class="lr-bottom-margin-40">
        If, on the other hand, the goal is to understand the relationships between the independent and dependent variables in your model, the R-squared is practically irrelevant. In that case, what matters is the regression coefficient and the statistical significance of the independent variable.
      </p>
      <h4 class="h5 bottom-margin-32">
        Standard Error of the Regression (SER)
      </h4>
      <p class="bottom-margin-32">
        The standard error of the regression (or <span class="bolded-texts">standard error of the estimate</span>) is another goodness-of-fit measure that shows the precision of your regression analysis. In other words, it represents the average distance of the observed values from the regression line. The smaller the number, the more confident you can be about your regression equation because observations are closer to the fitted line.
      </p>
      <img class="img bottom-margin-32" src="../../../../assets/images/calculators/linear-regression/linear-regression-16.webp" alt="Standard error graph.">
      <p class="bottom-margin-32">
        <span class="bolded-texts">Note:</span> SER is smaller when data points are closer to the regression line.
      </p>
      <p>The standard error of the regression (SER) for a simple linear regression can be calculated using the following formula:</p>
      <p class="formulas-overflow bottom-margin-32">
        \[Standard\ Error\ of\ Regression\ (SER)=\sqrt{\frac{SSE}{n-k-1}}\]
      </p>
      <p>Where:</p>
      <p>SSE is the sum of squares error and measures the unexplained variability by the regression.</p>
      <p>k is the number of independent variables in the regression model. For a simple linear regression, k is equal to 1.</p>
      <p>In practice, approximately 95% of the observations fall within + or -2 standard errors from the regression line. Conveniently, the standard error of the regression uses the measurement units of the dependent variable. For example, let’s assume that the amount of ice cream sold is proportional to the mean temperature. To test this hypothesis, you create a regression in which the dependent variable is the volume of ice cream sold, and the independent variable represents the mean temperature.</p>
      <p class="bottom-margin-32">
        Now, assume that the standard error of the regression is 6.88. In other words, the standard distance between the predicted and observed values is \$6.88.This means that, on average, the predicted values from the regression model differ from the observed values by approximately $6.88.
      </p>
      <p class="bolded-texts">
        What is a good standard error of the regression (SER)?
      </p>
      <p class="lr-bottom-margin-40">
        What constitutes a "good" standard error depends on the context and the research question. Generally, a standard error that is less than 10% of the mean value of the dependent variable is considered a good level of precision. But this is not a firm and fixed rule, and it may be necessary to consider other factors, such as the sample size, the complexity of the model, and the level of noise in the data. It’s also essential to compare the standard error to the standard deviation of the dependent variable to determine if it’s a significant source of variability in the data.
      </p>
      <h4 class="h5 bottom-margin-32">
        Coefficient of Determination vs Standard Error of the Regression
      </h4>
      <p>The coefficient of determination (<span class="bolded-texts">R-squared</span>) and <span class="bolded-texts">standard error of the regression (SER)</span> are “goodness of fit” measures that show how well the calculated linear regression equation fits the data.</p>
      <p>But there’s an essential distinction between them. <span class="bolded-texts">R-squared</span> represents the percentage of the dependent variable variance explained by the model. By contrast, the <span class="bolded-texts">standard error of the regression</span> provides the absolute measure of the average distance of data points from the regression line.</p>
      <p>Let's use an analogy related to the speed of a car.</p>
      <p>R-squared provides a relative measure of improvement without specifying the actual change in speed. If you require precise information on the speed increase, you may need more than the percentage to provide the desired level of detail. In this scenario, R squared reveals that the car went 70% faster than usual.</p>
      <p>But it matters how fast the car was traveling in the first place. If you want to know precisely how much “faster” 70% is, you must estimate the standard error of the regression. At 10 mph, a 70% increase would be 7 mph. And at 100 mph, the increase would be 70 mph.</p>
      <p>The standard error of the regression (SER) directly quantifies the actual increase in speed. It tells you that the car's speed increased by 70 mph, which is undeniably impressive and provides a clear understanding of the change.</p>
      <p>In summary, while R-squared presents a relative measure of improvement, SER provides a more tangible and explicit indication of the actual change in speed.</p>
      <p class="lr-bottom-margin-40">
        Our linear regression calculator provides both values, allowing you to interpret the results.
      </p>
      <h4 class="h5 bottom-margin-32">
        The Correlation Coefficient
      </h4>
      <p>The correlation coefficient is an essential measure in regression analysis. The symbol R denotes it and tells you how strong the linear relationship between two variables is. In the context of regression analysis, the correlation coefficient measures the strength and direction of the linear relationship between the independent and dependent variables.</p>
      <p class="lr-bottom-margin-40">
        The correlation coefficient can be any value between -1 and 1. A correlation of 1 (a perfect positive correlation) means that the variability of the second variable is explained by the first one.
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-17.webp" alt="Perfect correlation graph.">
      <p class="lr-bottom-margin-40">
        Conversely, a value of 0 indicates that the two variables are uncorrelated. In other words, knowing the actual value of variable one tells you very little about the importance of variable two.
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-18.webp" alt="No correlation graph.">
      <p class="lr-bottom-margin-40">
        At the other end of the line, a coefficient of -1 denotes a solid negative relationship; they move in opposite directions 100% of the time.
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-19.webp" alt="Perfect negative correlation graph.">
      <p class="lr-bottom-margin-40">
        Our linear regression calculator automatically generates the correlation coefficient and all other results.
      </p>
      <h4 class="h5 bottom-margin-32">
        The F-statistic
      </h4>
      <p class="lr-bottom-margin-40">
        Much like the <nuxt-link to="/calculators/tables/snd-table/" target="_blank">
          Z-statistic
        </nuxt-link> that follows a <nuxt-link to="/tutorials/statistics-tutorials/normal-distribution/" target="_blank">
          normal distribution
        </nuxt-link> and the <nuxt-link to="/calculators/tables/std-table/" target="_blank">
          T-statistic
        </nuxt-link> that follows a <nuxt-link to="/tutorials/statistics-tutorials/students-t-distribution/" target="_blank">
          Student’s T distribution
        </nuxt-link>, the F-statistic follows an F distribution.
      </p>
      <p class="f-statistic-img-heading">
        F-statistic
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-20.webp" alt="F-statistic graph.">
      <p>
        The F-statistic is a specific form of test for the overall significance of the model. It’s used to test the <nuxt-link to="/tutorials/statistics-tutorials/null-hypothesis/" target="_blank">
          null hypothesis
        </nuxt-link> that all regression coefficients in the model are equal to zero—indicating that the independent variable does not significantly affect the dependent variable.
      </p>
      <p>The null hypothesis is that beta 1 is equal to 0:</p>
      <p class="formulas-overflow">
        \[H_{0\ }:\ \ \beta_1=0\]
      </p>
      <p>And the alternative hypothesis is that beta 1 differs from 0:</p>
      <p class="formulas-overflow">
        \[H_{1\ }:\ \ \beta_1\neq0\]
      </p>
      <p>So, how do we interpret the results?</p>
      <p>If the beta is 0, then the independent variable has no impact on the dependent variable in the regression model. Therefore, our model is considered statistically insignificant.</p>
      <p>The F-statistic is calculated using the following formula:</p>
      <p class="formulas-overflow">
        \[F=\frac{MSR}{MSE}\]
      </p>
      <p>The mean sum of squares (MSR) is equal to the regression sum of squares (SSR) divided by the regression degrees of freedom:</p>
      <p class="formulas-overflow">
        \[MSR=\frac{SSR}{k}\]
      </p>
      <p>The latter equals 1 because the number of coefficients (k) is 1.</p>
      <p>MSE is the mean sum of squares due to error. It’s equal to the sum of squares error (SSE) divided by the residual degrees of freedom:</p>
      <p class="formulas-overflow">
        \[MSE=\frac{SSE}{n-k-1}\]
      </p>
      <p>The latter equals the total number of observations (n) minus k (the number of independent variables) minus 1.</p>
      <p>You’ll need to find the critical value in the f-table to determine whether your result is significant. The rule states that you can reject the null hypothesis if your F-statistic is higher than the critical value at a given significance level. The lower the F-statistic, the closer your model is to insignificance.</p>
      <p>For example, let’s say we have 1 regression degree of freedom (or DF 1) and 18 residual degrees of freedom (DF 2). This corresponds to a critical value of 4.41 at an alpha level 0.05. Any F value higher than 4.41 would mean we can reject the null hypothesis.</p>
      <p>Statistical software tools typically provide a probability or p-value for the F-statistic. You can also obtain it with our regression equation calculator.</p>
      <p>So, what does this number tell us?</p>
      <p>Its value gives us the probability that the null hypothesis cannot be rejected. In other words, you can find whether your results are statistically significant. Think of it as the probability that the regression model is wrong and has no merit. Unlike the F-statistics value, we would like this probability to be as small as possible.</p>
      <p>In practice, we establish a significance level and use it as a cutoff point. Using 1%, 5%, or 10% levels is expected. The model is significant if the p-value is less than that. And if it’s more significant, you should choose another independent variable.</p>
      <p class="lr-bottom-margin-40">
        If the p-value is smaller than a given significance level, the regression model as a whole is statistically significant.
      </p>
      <h4 class="h5 bottom-margin-32">
        The T-statistic
      </h4>
      <p class="lr-bottom-margin-40">
        In regression analysis, the T-statistic measures the degree to which the estimated value of a regression coefficient differs from zero. In other words, it tests whether the estimated coefficient significantly differs from zero.
      </p>
      <p>For example, for the intercept of the regression line, , the null hypothesis of this test is that equals zero:</p>
      <p class="formulas-overflow">
        \[H_{0\ }:\ \ \beta_0=0\]
      </p>
      <p>And the alternative hypothesis is that <span class="inline-formulas">\[b_0\]</span> differs from zero:</p>
      <p class="formulas-overflow">
        \[H_{1\ }:\ \ \beta_0\neq0\]
      </p>
      <p class="lr-bottom-margin-40">
        If a coefficient is 0 for the intercept or <span class="inline-formulas">\[b_0\]</span>, then the regression line crosses the y-axis at the origin:
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-21.webp" alt="Regression line crossing the y-axis at the origin graph.">
      <p>Similarly, the null hypothesis for the slope of the regression line, <span class="inline-formulas">\[b_1\]</span>, is that it is equal to 0:</p>
      <p class="formulas-overflow">
        \[H_{0\ }:\ \ \beta_1=0\]
      </p>
      <p>And the alternative hypothesis is that <span class="inline-formulas">\[b_1\]</span> differs from 0:</p>
      <p class="formulas-overflow">
        \[H_{1\ }:\ \ \beta_1\neq0\]
      </p>
      <p class="lr-bottom-margin-40">
        If <span class="inline-formulas">\[b_1\]</span> is 0, then 0 times x will always be 0 for any x, so we shall not consider this variable for the model. Graphically, that would mean that the regression line is horizontal and always goes through the intercept value.
      </p>
      <img class="img" src="../../../../assets/images/calculators/linear-regression/linear-regression-22.webp" alt="Horizontal regression line graph.">
      <p>The T-statistic is computed by dividing the coefficient by its standard error. For <span class="inline-formulas">\[b_1\]</span>, we have the following:</p>
      <p class="formulas-overflow">
        \[t_{b_1=\ \frac{\beta_1}{s_{\beta_1}}}\]
      </p>
      <p>Where the standard error is equal to:</p>
      <p class="formulas-overflow">
        \[S_{\beta_1}=\frac{\sqrt{MSE}}{\sqrt{\sum{(X-\bar{X})}^2}}\]
      </p>
      <p>Understandably, the smaller the standard error, the better. This implies that a higher t-value leads to a more reliable coefficient.</p>
      <p>Nowadays, statistics software or programming languages can quickly produce the output of these tests. You can also obtain this using our regression line calculator. All you need to do is interpret the output numbers.</p>
      <p>One essential indicator in this regard is the p-value. Simply put, a p-value measures the probability that an observed result occurred by chance instead of a particular pattern.</p>
      <p>So, how is the t-stat linked to the p-value? The software takes the t-statistic, weighs it against the values in the student’s t-distribution, and produces the p-value.</p>
      <p>This is what we are primarily interested in analyzing. Why? The p-values for the coefficients indicate whether the dependent variable is statistically significant. Think of it as the probability of an error. Unlike the t-value, we would like this probability to be as small as possible.</p>
      <p>For example, if the test produces a p-value of 0.0326, there is a 3.26% chance that the results happened randomly. If, in a different scenario, the p-value is 0.9429, the results have a 94.29% chance of being random.</p>
      <p>Consider the following important rule. The smaller the p-value, the stronger the evidence for rejecting the null hypothesis.</p>
      <p>Therefore, when you see a report with the results of statistical tests, look out for the p-value. Typically, the closer to zero, the better—depending on the hypotheses stated in that report.</p>
      <p>But how small is small enough? For that, researchers set a cut-off value known as the significance level.</p>
      <p>So, we follow the rule: When the p-value is less than the significance level, we can reject the null hypothesis that the coefficient equals zero. The cutoff or significance level is typically 1%, 5%, or 10%. We commonly use a 5% significance level.</p>
      <p>A p-value below 0.05 means that the variable is significant. Therefore, the coefficient is different from 0.</p>
      <p>Let’s refer to the earlier example, where we assumed that the amount of ice cream sold is proportional to the mean temperature. We find that the p-value of the dependent variable temperature is 0.001.</p>
      <p>What does this mean?</p>
      <p class="lr-bottom-margin-40">
        It tells us that temperature is a significant predictor of ice cream sales. So, we can use the regression model to forecast ice cream sales based on temperature. We can estimate the expected sales volume by inputting temperature values into the model and plan accordingly. Additionally, we can strategically adjust pricing and promotions based on temperature patterns. For example, during hot weather, we might consider offering discounts or launching targeted marketing campaigns to capitalize on increased demand.
      </p>
      <h4 class="h5 bottom-margin-32">
        Interpreting the Analysis of Variance (ANOVA) Table
      </h4>
      <p>ANOVA is a statistical method for determining whether the differences between the means of two or more groups are statistically significant. It’s commonly used in experimental and research studies to test hypotheses and draw conclusions about population means.</p>
      <p>ANOVA can be used in linear regression when the predictor variable is categorical. It provides information about the variability in a regression model.</p>
      <p>The ANOVA table is automatically generated by statistical software when performing regression analysis. It’s also part of our linear regression calculator’s results.</p>
      <p>In a linear regression model, the ANOVA table divides the sum of squares into individual components that provide information about the variability levels within the regression model. The table includes the following statistics:</p>
      <div class="last-image-gradient-wrapper gradient" :class="{ 'no-gradient-after': endScrolledAfter, 'no-gradient-before': endScrolledBefore }">
        <div class="last-image-scroll-wrapper" @scroll="handleScroll">
          <img
            class="img last-image" src="../../../../assets/images/calculators/linear-regression/linear-regression2-23.webp" alt="Example of a ANOVA table."
          >
        </div>
      </div>
      <ol class="anova-ol">
        <li><span class="bolded-texts">Degrees of freedom regression (DFR):</span> These are the degrees of freedom related to the SSR (sum of squares regression). In linear regression, DFR equals k (the number of independent variables in the model). Simply put, DFR is equal to 1.</li>
        <li><span class="bolded-texts">Degrees of freedom residual (DFE):</span> This is the degrees of freedom associated with the (SSE) sum of squares error. DFE equals the total number of observations minus k (the number of independent variables in the model) minus 1.</li>
        <li>
          <p><span class="bolded-texts">The sum of squares regression (SSR):</span> This is the sum of the differences between the predicted value and the mean of the dependent variable.</p>
          <p class="formulas-overflow">
            \[SSR=\sum_{i=1}^{n}{({\hat{y}}_i-\bar{y})}^2\]
          </p>
          <p><span class="inline-formulas">\[\hat{y}_i\]</span> – the predicted value of the dependent variable</p>
          <p><span class="inline-formulas">\[\bar{y}\]</span> – mean of the dependent variable</p>
          <p>In other words, this is the variation in the dependent variable explained by the independent variable. Think of it as a measure that describes how well your line fits the data. If the value of SSR is equal to the sum of squares total, your regression model captures all the observed variability and is perfect.</p>
        </li>
        <li>
          <p><span class="bolded-texts">The sum of squares residual (SSE)</span> is the variation in the dependent variable, which is not explained by the independent variable. Mathematically, SSE is the difference between the observed value and the predicted value:</p>
          <p class="formulas-overflow">
            \[SSE=\sum_{i=1}^{n}\varepsilon_i^2\]
          </p>
          <p><span class="inline-formulas">\[ε_i\]</span> – the difference between the actual value of the dependent variable and the predicted value</p>
          <p class="inline-formulas">
            \[ε_i=y_i-\hat{y}_i\]
          </p>
          <p>The smaller the error, the better the estimation power of the regression. That’s why we usually want to minimize the error. This determinant is often called the ‘residual sum of squares’ (or RSS), where ‘residual’ denotes remaining or unexplained. It becomes even more confusing when I tell you that some people refer to it as SSR, which makes it unclear whether we are talking about the sum of squares due to regression or the sum of squared residuals.</p>
        </li>
        <li>
          <p><span class="bolded-texts">The sum of squares total (SST)</span> is equal to the squared differences between the observed dependent variable and its mean:</p>
          <p class="formulas-overflow">
            \[SST=\sum_{i=1}^{n}{(y_i-\bar{y})}^2\]
          </p>
          <p><span class="inline-formulas">\[y_i\]</span> – observed dependent variable</p>
          <p><span class="inline-formulas">\[\bar{y}\]</span> – mean of the dependent variable</p>
          <p>You can think of the SST as the dispersion of the observed variables around the mean. It’s a measure of the total variability of the dataset.</p>
        </li>
        <li>
          <p><span class="bolded-texts">The mean square regression (MSR)</span> is equal to the SSR divided by the regression degrees of freedom:</p>
          <p class="formulas-overflow">
            \[MSR=\frac{SSR}{DFR}\]
          </p>
        </li>
        <li>
          <p><span class="bolded-texts">The mean square residual (MSE)</span> is equal to the SSE divided by the degrees of freedom residual (DFE):</p>
          <p class="formulas-overflow">
            \[MSE=\frac{SSE}{DFE}\]
          </p>
        </li>
        <li>
          <p><span class="bolded-texts">F-statistic</span> is used for testing the overall significance of the regression model and is equal to calculated by dividing the regression mean sum of squares (MSR) by the residual mean sum of squares (MSE):</p>
          <p class="formulas-overflow">
            \[F-statistic=\frac{MSR}{MSE}\]
          </p>
          <p>
            You’ll need to find the critical value in the <nuxt-link to="/calculators/tables/fd-table/" target="_blank">
              f-table
            </nuxt-link> to determine whether your result is significant. The rule states that if your F-statistic is higher than the critical value at a given significance level, you can reject the null hypothesis. The lower the F-statistic, the closer to a non-significant model.
          </p>
        </li>
        <li>
          <p>The significance level is the p-value associated with the F-statistic, which indicates the probability that the null hypothesis—the independent variable does not have a significant effect on the dependent variable—cannot be rejected. In other words, you can determine your results as statistically substantial. Think of it as the probability that the regression model is wrong and has no merit. Unlike the F-statistics value, we would like this probability to be as small as possible.</p>
          <p class="formulas-overflow">
            \[p-value=1-{cdf}_{F,d_1,d_2}(F\ score)\]
          </p>
          <p>Where:</p>
          <p>CDF is the cumulative distribution function of the F-distribution at a specific F value.</p>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      endScrolledAfter: false,
      endScrolledBefore: true,
    };
  },
  mounted() {
    this.renderMathJax();
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
    handleScroll(event) {
      const { scrollLeft, scrollWidth, offsetWidth } = event.target;
      const contentWidth = scrollWidth - offsetWidth;
      if (contentWidth <= scrollLeft + 10) {
        this.endScrolledAfter = true;
      } else {
        this.endScrolledAfter = false;
      }

      if (scrollLeft - 10 >= 0) {
        this.endScrolledBefore = false;
      } else {
        this.endScrolledBefore = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.articles-container {
  .article-section {
    p {
      margin: 0 0 var(--spacing-20);

      &:last-of-type {
        margin: 0;
      }
    }

    .lr-bottom-margin-40 {
      margin-bottom: var(--spacing-40);
    }

    .img {
      margin: 0 0 var(--spacing-40);
    }

    .last-image-gradient-wrapper {
      margin: var(--spacing-40) 0 0;

      &.gradient {
        position: relative;

        &:after {
          content: "";
          width: 31px;
          height: calc(100% - 38px);
          position: absolute;
          right: 0;
          top: 0;
          background: linear-gradient(270deg, var(--main-light) 1.64%, rgba(255, 255, 255, 0) 84.62%);
        }

        &:before {
          content: "";
          width: 31px;
          height: calc(100% - 38px);
          position: absolute;
          left: 0;
          top: 0;
          background: linear-gradient(90deg, var(--main-light) 1.64%, rgba(255, 255, 255, 0) 84.62%);
        }
      }

      &.no-gradient-after {
        &:after {
          content: none;
        }
      }

      &.no-gradient-before {
        &:before {
          content: none;
        }
      }

      .last-image-scroll-wrapper {
        overflow: auto;
        padding-bottom: var(--spacing-30);

        &::-webkit-scrollbar {
          height: 6px;
        }

        &::-webkit-scrollbar-thumb {
          background: var(--main-light-shade-3);
          border-radius: 10px;
        }

        .last-image {
          max-width: unset;
          align-self: flex-start;
          margin: 0;
        }
      }
    }

    .first-img-heading,
    .third-img-heading,
    .fourth-img-heading {
      text-align: center;
      color: var(--accent-shade-3);
      font-weight: 600;
      font-size: 24px;
      margin: 0;
    }

    .second-img-heading,
    .four-graphs-img-heading,
    .four-graphs-img-heading-second,
    .f-statistic-img-heading {
      text-align: center;
      font-size: 26px;
      font-weight: 600;
      margin: 0;
      color: var(--accent-shade-3);
    }

    .four-graphs-img-heading-second {
      margin: var(--spacing-4) 0 var(--spacing-16);
    }

    .f-statistic-img-heading {
      margin: 0 0 var(--spacing-12);
    }

    .lr-formulas {
      margin: var(--spacing-4) 0 var(--spacing-24);
    }

    .steps-ol {
      li {
        margin: var(--spacing-24) 0;

        &:nth-child(1) {
          margin-top: var(--spacing-40);
        }
      }
    }

    .anova-ol {
      margin-top: var(--spacing-40);
    }

    .top-margin-32 {
      margin-top: var(--spacing-32);
    }

    .bottom-margin-32 {
      margin-bottom: var(--spacing-32);
    }
  }

  .ul-padding-inline-start ul {
    padding-inline-start: 20px;
    list-style-position: outside;
  }
}

@include bp($bp-laptop) {
  .articles-container {
    .article-section {
      .first-img-heading,
      .third-img-heading,
      .fourth-img-heading,
      .second-img-heading,
      .four-graphs-img-heading,
      .four-graphs-img-heading-second,
      .f-statistic-img-heading {
        font-size: 22px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .articles-container {
    .article-section {
      .first-img-heading,
      .third-img-heading,
      .fourth-img-heading,
      .second-img-heading,
      .four-graphs-img-heading,
      .four-graphs-img-heading-second,
      .f-statistic-img-heading {
        font-size: 18px;
      }

      .first-img-heading {
        margin-bottom: var(--spacing-6);
      }

      .last-image-gradient-wrapper {
        .last-image-scroll-wrapper {
          .last-image {
            height: 245px;
          }
        }
      }
    }
  }
}

@import '../../../../assets/sass/ds/calculators/articles.scss';
</style>
